"use client";

import type { Breakpoint } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import type { NavSectionProps } from "src/components/nav-section";
import type { HeaderSectionProps, LayoutSectionProps, MainSectionProps, } from "../core";
import { HeaderSection, layoutClasses, LayoutSection, MainSection, } from "../core";

import { merge } from "es-toolkit";
import { useBoolean } from "minimal-shared/hooks";

import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import { iconButtonClasses } from "@mui/material/IconButton";


import { Logo } from "src/components/logo";
import { useSettingsContext } from "src/components/settings";

import { useAuthContext, useMockadAnvandare } from "src/auth/hooks";

import { NavMobile } from "./nav-mobile";
import { VerticalDivider } from "./content";
import { NavVertical } from "./nav-vertical";
import { NavHorizontal } from "./nav-horizontal";
import { _account } from "../nav-config-account";
import { Searchbar } from "../components/searchbar";
import { MenuButton } from "../components/menu-button";
import { AccountDrawer } from "../components/account-drawer";
import { AttGöraPopover } from "../components/att-gora-popover";
import { SettingsButton } from "../components/settings-button";
import { ContactsPopover } from "../components/contacts-popover";
import { OrganisationPopover } from "../components/organisation-popover";
import { dashboardLayoutVars, dashboardNavColorVars } from "./css-vars";
import { NotificationsDrawer } from "../components/notifications-drawer";
import { utanMomsposter, hämtaSidebarsFörAnvändare } from "../nav-config-dashboard";
import { skapaBehörighetskontroll } from "../nav-permissions";
import { SynligaRutterGuard } from "./synliga-rutter-guard";
import { useAppContext } from "src/context/app";
import { useMenyInställningar, tillSynlighetskarta } from "src/hooks/use-meny-installningar";
import { useOrganisationsbytesNyckel } from "src/hooks/use-organisationsbyte";
import { DemoCompanionBar } from "src/sections/demo/components/demo-companion-bar";

// ----------------------------------------------------------------------

type LayoutBaseProps = Pick<LayoutSectionProps, "sx" | "children" | "cssVars">;

export type DashboardLayoutProps = LayoutBaseProps & {
  layoutQuery?: Breakpoint;
  slotProps?: {
    header?: HeaderSectionProps;
    nav?: {
      data?: NavSectionProps["data"];
    };
    main?: MainSectionProps;
  };
};

export function DashboardLayout({
  sx,
  cssVars,
  children,
  slotProps,
  layoutQuery = "lg",
}: DashboardLayoutProps) {
  const theme = useTheme();
  // const { anvandare } = useAuthContext();
  const { anvandare, organisationer } = useAuthContext();
  // const { anvandare } = useAuthContext();
  const { organisation, valdOrganisation } = useAppContext();
  const { inställningar: menyInställningar, klar: menyInställningarKlara } =
    useMenyInställningar(organisation?.id);
  // Byter organisationen monteras innehållet om — sidan hämtar då sin data för
  // den nya föreningen utan att hela fönstret laddas om. Sidobaren behöver
  // ingen nyckel: den byggs av `menyInställningar`, som redan hämtas per org.
  //
  // Nyckeln följer det råa valet, inte `organisation`: byten till och från
  // Privatperson ger `organisation: undefined` och skulle annars inte räknas.
  const innehållsNyckel = useOrganisationsbytesNyckel(valdOrganisation?.id);
  const settings = useSettingsContext();

  const navVars = dashboardNavColorVars(
    theme,
    settings.state.navColor,
    settings.state.navLayout
  );

  const { value: open, onFalse: onClose, onTrue: onOpen } = useBoolean();

  // const navData = slotProps?.nav?.data ?? sidebarsFörBrfAppOchKonsultApp;
  // Momsmenyn döljs för en organisation som inte är momsregistrerad. Den redovisar ingen moms,
  // och backend nekar deklarationerna ändå — se Momsregel. `momsregistrerad` är odefinierad
  // medan organisationen hämtas, och då visas menyn som förut.
  const navData =
    slotProps?.nav?.data ??
    (organisation?.momsregistrerad === false
      ? utanMomsposter(
          hämtaSidebarsFörAnvändare(anvandare, tillSynlighetskarta(menyInställningar))
        )
      : hämtaSidebarsFörAnvändare(anvandare, tillSynlighetskarta(menyInställningar)));

  const isNavMini = settings.state.navLayout === "mini";
  const isNavHorizontal = settings.state.navLayout === "horizontal";
  const isNavVertical = isNavMini || settings.state.navLayout === "vertical";

  // Nav-komponenterna filtrerar posterna en gång till när de renderar. Den
  // kontrollen måste dela rollexpansion med `hämtaSidebarsFörAnvändare` — en rå
  // jämförelse mot rollnamnen döljer allt vars `allowedRoles` är en
  // behörighetsgrupp (`boende`, `ekonomi`) i stället för ett rollnamn.
  const canDisplayItemByRole = skapaBehörighetskontroll(anvandare);

  const renderHeader = () => {
    const headerSlotProps: HeaderSectionProps["slotProps"] = {
      container: {
        maxWidth: false,
        sx: {
          ...(isNavVertical && { px: { [layoutQuery]: 5 } }),
          ...(isNavHorizontal && {
            bgcolor: "var(--layout-nav-bg)",
            height: { [layoutQuery]: "var(--layout-nav-horizontal-height)" },
            [`& .${iconButtonClasses.root}`]: {
              color: "var(--layout-nav-text-secondary-color)",
            },
          }),
        },
      },
    };

    const headerSlots: HeaderSectionProps["slots"] = {
      topArea: (
        <Alert severity="info" sx={{ display: "none", borderRadius: 0 }}>
          This is an info Alert.
        </Alert>
      ),
      bottomArea: isNavHorizontal ? (
        <NavHorizontal
          data={navData}
          layoutQuery={layoutQuery}
          cssVars={navVars.section}
          checkPermissions={canDisplayItemByRole}
        />
      ) : null,
      leftArea: (
        <>
          {/** @slot Nav mobile */}
          <MenuButton
            onClick={onOpen}
            sx={{
              mr: 1,
              ml: -1,
              [theme.breakpoints.up(layoutQuery)]: { display: "none" },
            }}
          />
          <NavMobile
            data={navData}
            open={open}
            onClose={onClose}
            cssVars={navVars.section}
            checkPermissions={canDisplayItemByRole}
          />

          {/** @slot Logo */}
          {isNavHorizontal && (
            <Logo
              sx={{
                display: "none",
                [theme.breakpoints.up(layoutQuery)]: { display: "inline-flex" },
              }}
            />
          )}

          {/** @slot Divider */}
          {isNavHorizontal && (
            <VerticalDivider
              sx={{ [theme.breakpoints.up(layoutQuery)]: { display: "flex" } }}
            />
          )}

          {/** @slot Workspace popover */}
          <OrganisationPopover
            organisationer={organisationer}
            sx={{
              ...(isNavHorizontal && {
                color: "var(--layout-nav-text-primary-color)",
              }),
            }}
          />
        </>
      ),
      centerArea: (
        /** @slot Att göra – myndighetsdatum och förfallna poster */
        <AttGöraPopover />
      ),
      rightArea: (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 0, sm: 0.75 },
          }}
        >
          {/** @slot Searchbar */}
          <Searchbar data={navData} />

          {/** @slot Language popover */}
          {/*<LanguagePopover*/}
          {/*  data={[*/}
          {/*    { value: "en", label: "English", countryCode: "GB" },*/}
          {/*    { value: "fr", label: "French", countryCode: "FR" },*/}
          {/*    { value: "vi", label: "Vietnamese", countryCode: "VN" },*/}
          {/*    { value: "cn", label: "Chinese", countryCode: "CN" },*/}
          {/*    { value: "ar", label: "Arabic", countryCode: "SA" },*/}
          {/*  ]}*/}
          {/*/>*/}

          {/** @slot Notifications popover — hämtar sin egen data från /api/notifieringar/v1 */}
          <NotificationsDrawer />

          {/** @slot Contacts popover */}
          <ContactsPopover />

          {/** @slot Settings button */}
          <SettingsButton />

          {/** @slot Account drawer */}
          <AccountDrawer data={_account} />
        </Box>
      ),
    };

    return (
      <HeaderSection
        layoutQuery={layoutQuery}
        disableElevation={isNavVertical}
        {...slotProps?.header}
        slots={{ ...headerSlots, ...slotProps?.header?.slots }}
        slotProps={merge(headerSlotProps, slotProps?.header?.slotProps ?? {}) as any}
        sx={slotProps?.header?.sx}
      />
    );
  };

  const renderSidebar = () => (
    <NavVertical
      data={navData}
      isNavMini={isNavMini}
      layoutQuery={layoutQuery}
      cssVars={navVars.section}
      checkPermissions={canDisplayItemByRole}
      onToggleNav={() =>
        settings.setField(
          "navLayout",
          settings.state.navLayout === "vertical" ? "mini" : "vertical"
        )
      }
    />
  );

  const renderFooter = () => null;

  const renderMain = () => (
    <MainSection key={innehållsNyckel} {...slotProps?.main}>
      <DemoCompanionBar />
      <SynligaRutterGuard navData={navData} klar={menyInställningarKlara}>
        {children}
      </SynligaRutterGuard>
    </MainSection>
  );

  return (
    <LayoutSection
      /** **************************************
       * @Header
       *************************************** */
      headerSection={renderHeader()}
      /** **************************************
       * @Sidebar
       *************************************** */
      sidebarSection={isNavHorizontal ? null : renderSidebar()}
      /** **************************************
       * @Footer
       *************************************** */
      footerSection={renderFooter()}
      /** **************************************
       * @Styles
       *************************************** */
      cssVars={{ ...dashboardLayoutVars(theme), ...navVars.layout, ...cssVars }}
      sx={[
        {
          [`& .${layoutClasses.sidebarContainer}`]: {
            [theme.breakpoints.up(layoutQuery)]: {
              pl: isNavMini
                ? "var(--layout-nav-mini-width)"
                : "var(--layout-nav-vertical-width)",
              transition: theme.transitions.create(["padding-left"], {
                easing: "var(--layout-transition-easing)",
                duration: "var(--layout-transition-duration)",
              }),
            },
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {renderMain()}
    </LayoutSection>
  );
}
